import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, children, testid, leading, details }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex items-center gap-3">
          {leading}
          <div>
            {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
            <h1 className="text-4xl sm:text-5xl font-bold uppercase tracking-tight" data-testid={testid}>{title}</h1>
            {subtitle}
          </div>
        </div>
        {details}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Kpi({ label, value, color = "#FF3B30", to, testid, sub }) {
  const body = (
    <div className="panel kpi-bar p-5 h-full row-hover" style={{ "--kpi": color }} data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="font-display text-4xl font-bold mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

const STATUS = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  paid: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  converted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  template: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  ai_draft: "bg-amber-500/10 text-amber-400 border-amber-500/40",
  draft: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  open: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  scheduled: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  new: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  contacted: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
  past_due: "bg-red-500/10 text-red-400 border-red-500/30",
  expired: "bg-red-500/10 text-red-400 border-red-500/30",
  inactive: "bg-slate-700/30 text-slate-400 border-slate-600/30",
  lost: "bg-slate-700/30 text-slate-500 border-slate-600/30",
  void: "bg-slate-700/30 text-slate-500 border-slate-600/30",
  archived: "bg-slate-700/30 text-slate-500 border-slate-600/30",
  rejected: "bg-slate-700/30 text-slate-500 border-slate-600/30",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  reviewed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

export function StatusBadge({ status, testid }) {
  const label = status === "ai_draft" ? "AI draft · needs review" : (status || "none").replace("_", " ");
  return (
    <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono-k uppercase tracking-wider ${STATUS[status] || STATUS.draft}`}>
      {label}
    </span>
  );
}

export function Section({ title, action, children, testid, className = "" }) {
  return (
    <section className={`panel p-5 sm:p-6 fade-up ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4 gap-2">
          <h3 className="font-display text-xl font-semibold uppercase tracking-wide">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Empty({ text, testid }) {
  return <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-[#1E293B] rounded-md" data-testid={testid}>{text}</div>;
}

export function AiBanner({ flags = [], model }) {
  return (
    <div className="ai-draft rounded-md p-4 mb-6" data-testid="ai-draft-banner">
      <div className="flex items-center gap-2 text-amber-400 font-mono-k text-xs uppercase tracking-[0.2em]">
        <Sparkles size={14} /> AI draft · pending coach review {model && <span className="text-amber-500/60">({model})</span>}
      </div>
      <p className="text-sm text-slate-300 mt-2">This plan was generated by AI. Edit anything below, then approve. Clients never see a draft until you approve it.</p>
      {flags.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="ai-guardrail-flags">
          {flags.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-red-300"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {f}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
